/**
 * DANH SÁCH QUÁN — /venue
 *
 * Trang công khai, nằm ngoài Layout như trang chi tiết quán: link gửi được cho
 * khách chưa có tài khoản.
 *
 * Thẻ quán dùng đúng khối `.hero.venue-hero` của khối "Quán nổi bật" ở trang chủ,
 * không dựng kiểu thẻ thứ hai: hai chỗ hiện cùng một thứ mà trông khác nhau thì
 * người dùng phải học lại từ đầu, và sửa một chỗ là quên chỗ kia.
 *
 * Backend xếp quán nổi bật lên trước rồi mới tới tên; trang này giữ nguyên thứ tự
 * nhận được, không sắp lại theo số bàn hay theo khu vực.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import BilliardLoader from "../../components/BilliardLoader";
import TIcon from "../../components/ui/TIcon";
import { useVenueList } from "../../hooks/venue";
import "./venue.css";

/** Đợi người ta gõ xong rồi mới gọi API, thay vì mỗi phím một lượt. */
const CHO_GO_XONG_MS = 350;

const VenueListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Từ khoá nằm trên URL để một kết quả tìm còn chia sẻ hoặc tải lại được.
  const tuUrl = searchParams.get("q") ?? "";
  const [oGo, setOGo] = useState(tuUrl);
  const [tuKhoa, setTuKhoa] = useState(tuUrl);

  useEffect(() => {
    document.title = "Quán billiards — Biadi";
  }, []);

  useEffect(() => {
    const hen = setTimeout(() => {
      setTuKhoa(oGo.trim());
      setSearchParams(oGo.trim() ? { q: oGo.trim() } : {}, { replace: true });
    }, CHO_GO_XONG_MS);

    return () => clearTimeout(hen);
    // setSearchParams đổi tham chiếu mỗi lần render; đưa vào đây là hẹn giờ bị đặt lại liên tục.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oGo]);

  const { venues, loading, failed, hasNextPage, loadMore } = useVenueList(tuKhoa);

  return (
    <div className="venue main app-shell no-tabbar px-5 relative top-0">
      <div className="vn-nav">
        <button type="button" className="vn-back" onClick={() => navigate(-1)} aria-label="Quay lại">
          <TIcon name="chevron-left" />
        </button>
        <span className="vn-nav-title">QUÁN BILLIARDS</span>
      </div>

      <div className="vn-search">
        <TIcon name="search" />
        <input
          type="search"
          value={oGo}
          onChange={(e) => setOGo(e.target.value)}
          placeholder="Tìm theo tên quán, khu vực…"
          aria-label="Tìm quán"
        />
      </div>

      {/* Lần tải đầu mới che cả trang. Đang có danh sách mà tìm tiếp thì giữ nguyên
          những gì đang hiện - nháy cả trang mỗi lần gõ là khó chịu hơn hẳn. */}
      {loading && venues.length === 0 ? <BilliardLoader /> : null}

      {failed ? (
        <div className="ui-card empty-state" style={{ marginTop: 60 }}>
          <TIcon name="alert-triangle" />
          <div className="t">Không tải được danh sách quán</div>
          <div className="h">Kiểm tra kết nối rồi thử lại.</div>
        </div>
      ) : null}

      {!loading && !failed && venues.length === 0 ? (
        <div className="ui-card empty-state" style={{ marginTop: 60 }}>
          <TIcon name="search-off" />
          <div className="t">{tuKhoa ? "Không tìm thấy quán nào" : "Chưa có quán nào"}</div>
          {tuKhoa ? <div className="h">Thử một từ khoá ngắn hơn.</div> : null}
        </div>
      ) : null}

      <div className="vn-list">
        {venues.map((v) => (
          <button
            key={v.id}
            type="button"
            className="hero venue-hero"
            onClick={() => navigate(`/venue/${v.id}`)}
          >
            {v.coverImage ? <img src={v.coverImage} alt="" /> : null}
            <div className="overlay" />
            {v.logo ? <img className="venue-mark" src={v.logo} alt="" aria-hidden /> : null}
            <div className="content">
              {v.area ? <span className="ui-badge">{v.area}</span> : null}
              <span className="title">{v.name}</span>
              <span className="meta">
                {v.tableCount} bàn
                {v.headline ? ` · ${v.headline}` : ""}
              </span>
              <span className="hero-cta">XEM QUÁN</span>
            </div>
          </button>
        ))}
      </div>

      {hasNextPage ? (
        <button type="button" className="vn-btn vn-more" onClick={loadMore} disabled={loading}>
          {loading ? "Đang tải…" : "Xem thêm"}
        </button>
      ) : null}
    </div>
  );
};

export default VenueListPage;
